// Save-response reconciliation — IDENTITY-ONLY by design.
//
// The pedigree-config POST exists to deliver exactly two server-minted facts
// back to the client: each member's `upn` and its derived `familyMemberId`.
// The response used to echo the WHOLE posted member snapshot, and the client
// spread it row-by-row over live state ({ ...member, ...echoed }) — so any
// member edited between save-fire and response-arrival was silently REVERTED
// to the snapshot. In a rapid Add Relative burst that severed the just-added
// siblings' inbound edges (parents' childrenIds, sibs' siblingIds) while the
// victims kept their own fatherId/motherId: they became unreachable singleton
// components, were hoisted to the founders' row, and the jumbled mid-burst
// order was captured into establishedOrder and persisted (G-1-01424, SS-02/03;
// see claude-MDs/PLAN-pedigree-rapid-add-race-and-click-rebake.md).
//
// Merging ONLY the identity fields makes the response-arrival commutative with
// any edit that landed in between — the race window still exists, but nothing
// structural rides on it any more.

const IDENTITY_FIELDS = ['upn', 'familyMemberId'];

// True when an id is a persisted MongoDB ObjectId (24-hex). This is the
// contract the reconciliation below establishes: after the save response
// lands, member._id/uid are backfilled to String(row._id). Until then a fresh
// member's uid is a client-minted placeholder — truthy, but NOT usable
// against endpoints that cast to ObjectId (promote-proband would 500, and
// chart-proband would clear every flag before failing). Guard with this,
// never with a bare truthiness check.
export function isPersistedMemberId(id) {
  return /^[0-9a-fA-F]{24}$/.test(String(id ?? ''));
}

// Mint a client-side member uid that is BORN STABLE: a 24-hex ObjectId
// string. The backend member upsert branches on ObjectId-validity of uid and
// upserts by {_id: ObjectId(uid)}, so a client-minted hex id BECOMES the
// FamilyChanged _id — the save echo then has row._id === member.uid and no
// uid-keyed state (pins, establishedOrder, undo history, saved views, hidden
// overlays) can ever hold a dead key across the save round-trip. Collision
// risk is negligible (12 random bytes) and the {_id} upsert path was already
// reachable by any OID-shaped uid.
export function mintObjectIdHex() {
  const bytes = new Uint8Array(12);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Test-env fallback only (jsdom without WebCrypto) — every supported
    // browser has crypto.getRandomValues.
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Pure: returns a new records array with the server-assigned identity fields
// applied by pid. Never touches structure (fatherId/motherId/childrenIds/
// siblingIds/spouseIds) or any other member state.
export function applyIdentityReconciliation(records, familyData) {
  if (!Array.isArray(familyData) || familyData.length === 0) return records;
  const byPid = new Map(familyData.map((row) => [row.pid, row]));
  return records.map((member) => {
    const row = byPid.get(member.pid);
    if (!row) return member;
    const patch = {};
    for (const f of IDENTITY_FIELDS) {
      if (row[f] !== undefined && row[f] !== null) patch[f] = row[f];
    }
    // Persist server _id as both _id and uid. _id is needed so MakeProband can
    // resolve the MongoDB identifier in the same session (without a full reload).
    // uid must match so PedigreeConfig stores the correct key for reload.
    // Since uids are born stable (mintObjectIdHex in createPerson), uid
    // already equals String(row._id) and this is a no-op safety net — it only
    // rewrites for members created by pre-born-stable sessions/bundles.
    if (row._id) {
      patch._id = String(row._id);
      patch.uid = String(row._id);
    }
    return Object.keys(patch).length ? { ...member, ...patch } : member;
  });
}
